import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { APP_DEFAULT_PATH } from 'config';
import { useAuth } from 'contexts/AuthContext';

function isUserBlacklisted(user) {
  return !!user?.is_blacklisted;
}

function isSubscriptionExpired(user) {
  if (!user) return false;
  if (user.role === 'admin') return false;
  const expiresAt = user.subscription_expires_at;
  if (!expiresAt) return false;
  const ts = Date.parse(expiresAt.replace(' ', 'T'));
  if (Number.isNaN(ts)) return false;
  return ts < Date.now();
}

function FullPageLoader() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

export function ProtectedRoute({ children }) {
  const { loading, authenticated, user } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (isUserBlacklisted(user) && location.pathname !== '/account-blocked') {
    return <Navigate to="/account-blocked" replace />;
  }
  if (isSubscriptionExpired(user) && location.pathname !== '/get-subscription') {
    return <Navigate to="/get-subscription" replace />;
  }
  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node
};

export function PublicOnlyRoute({ children }) {
  const { loading, authenticated, user } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (authenticated) {
    // New Google users finish profile on the login page before entering the app.
    if (!user?.profile_completed && location.pathname === '/login') {
      return children;
    }
    if (isSubscriptionExpired(user)) {
      return <Navigate to="/get-subscription" replace />;
    }
    return <Navigate to={APP_DEFAULT_PATH} replace />;
  }
  return children;
}

PublicOnlyRoute.propTypes = {
  children: PropTypes.node
};

/** Requires authenticated admin (use inside ProtectedRoute). */
export function AdminRoute({ children }) {
  const { loading, isAdmin } = useAuth();

  if (loading) return <FullPageLoader />;
  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }
  return children;
}

AdminRoute.propTypes = {
  children: PropTypes.node
};

